const WIDTH = 560
const HEIGHT = 350
const PAD = 26

/**
 * Chromium rasterises SVG on the main thread. A dense panel's 1.6 MB stackup
 * can block for over a second just to make a 560 px card, which lands in the
 * middle of an import. Skip the preview for those; the project card falls back
 * to the brand mark and the board itself is unaffected.
 */
const MAX_THUMB_SVG_LENGTH = 800_000

/** Rasterise the top SVG into a small WebP card image. */
export async function makeThumbnail(svg: string): Promise<string> {
  if (svg.length > MAX_THUMB_SVG_LENGTH) return ''
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  try {
    const image = new Image()
    image.decoding = 'async'
    image.src = url
    await image.decode()

    const canvas = document.createElement('canvas')
    canvas.width = WIDTH
    canvas.height = HEIGHT
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''

    ctx.fillStyle = '#080a0d'
    ctx.fillRect(0, 0, WIDTH, HEIGHT)

    // Faint workbench grid, matching the canvas behind the board.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.035)'
    ctx.lineWidth = 1
    for (let x = 0.5; x < WIDTH; x += 28) {
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x, HEIGHT)
      ctx.stroke()
    }
    for (let y = 0.5; y < HEIGHT; y += 28) {
      ctx.beginPath()
      ctx.moveTo(0, y)
      ctx.lineTo(WIDTH, y)
      ctx.stroke()
    }

    const naturalWidth = image.naturalWidth || image.width || WIDTH
    const naturalHeight = image.naturalHeight || image.height || HEIGHT
    const scale = Math.min((WIDTH - PAD * 2) / naturalWidth, (HEIGHT - PAD * 2) / naturalHeight)
    const drawWidth = naturalWidth * scale
    const drawHeight = naturalHeight * scale
    const x = (WIDTH - drawWidth) / 2
    const y = (HEIGHT - drawHeight) / 2

    ctx.save()
    ctx.shadowColor = 'rgba(0, 0, 0, 0.55)'
    ctx.shadowBlur = 20
    ctx.shadowOffsetY = 6
    ctx.drawImage(image, x, y, drawWidth, drawHeight)
    ctx.restore()

    try {
      return canvas.toDataURL('image/webp', 0.82)
    } catch {
      return canvas.toDataURL('image/png')
    }
  } catch {
    return ''
  } finally {
    URL.revokeObjectURL(url)
  }
}
